/**
 * dataRows — which key of a data artifact's JSON body actually holds the rows.
 *
 * WHY THIS IS A SHARED MODULE. Backend data artifacts do not agree on one key. A
 * SOQL result carries `rows`; a graph/search result carries `results`; an
 * EXHAUSTIVE graph run additionally carries `full_list` + `full_list_count`, where
 * `results` is only the truncated head the agent reasoned over and `full_list` is
 * the complete answer. Two readers of that payload — the artifact card's table and
 * the report exporter's evidence appendix — must pick the SAME source, or the file
 * a user downloads disagrees with the card they downloaded it from. The precedence
 * lives here once.
 *
 * `full_list` wins because it is, by construction, the superset: an artifact only
 * carries it when the run was exhaustive, and in that case `results` is a preview
 * of it.
 */

export type DataRowSource = "full_list" | "results" | "rows" | "records" | "none";

export interface PickedDataRows {
  /** Row objects, normalized — scalars are wrapped so a bare string list still tables. */
  rows: Array<Record<string, unknown>>;
  /** Rows the ARTIFACT claims to hold, which can exceed `rows.length` when the
   *  payload itself was capped upstream. Never under-reports `rows.length`. */
  total: number;
  source: DataRowSource;
}

/** Row-source keys in precedence order. */
const ROW_KEYS: Array<Exclude<DataRowSource, "none">> = ["full_list", "results", "rows", "records"];

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/**
 * Normalize one array entry to a row object. A list of scalars (class names, ids —
 * what an inventory answer usually is) is wrapped as `{ value }` rather than
 * dropped: the alternative was an empty table under a "412 rows" header.
 */
function toRow(entry: unknown): Record<string, unknown> | null {
  if (entry === null || entry === undefined) return null;
  if (typeof entry === "object") {
    if (Array.isArray(entry)) return { value: entry };
    return entry as Record<string, unknown>;
  }
  return { value: entry };
}

function normalizeRows(value: unknown): Array<Record<string, unknown>> | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const out: Array<Record<string, unknown>> = [];
  for (const entry of value) {
    const row = toRow(entry);
    if (row) out.push(row);
  }
  return out.length ? out : null;
}

/**
 * Pick the row source out of a parsed data-artifact body.
 * Anything that is not an object, or carries no usable array, reads as `none`.
 */
export function pickDataRows(parsed: unknown): PickedDataRows {
  const empty: PickedDataRows = { rows: [], total: 0, source: "none" };
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    // A body that is itself an array of rows is still a data result.
    const bare = normalizeRows(parsed);
    if (!bare) return empty;
    return { rows: bare, total: bare.length, source: "rows" };
  }
  const obj = parsed as Record<string, unknown>;
  for (const key of ROW_KEYS) {
    const rows = normalizeRows(obj[key]);
    if (!rows) continue;
    const declared =
      key === "full_list" && isFiniteNumber(obj.full_list_count)
        ? obj.full_list_count
        : isFiniteNumber(obj.totalSize)
          ? obj.totalSize
          : isFiniteNumber(obj.total_count)
            ? obj.total_count
            : rows.length;
    // A declared total below what we actually hold is a backend bug, not a fact to
    // print — clamp rather than tell the user there are fewer rows than are shown.
    return { rows, total: Math.max(declared, rows.length), source: key };
  }
  // A bare aggregate (SELECT COUNT()) has a total and no rows at all.
  const total = isFiniteNumber(obj.totalSize)
    ? obj.totalSize
    : isFiniteNumber(obj.full_list_count)
      ? obj.full_list_count
      : 0;
  return { rows: [], total, source: "none" };
}

/**
 * Column order for a row set: the artifact's declared `columns` when it has them,
 * otherwise the union of the first `sample` rows' keys. `attributes` is Salesforce
 * REST bookkeeping (type + url) and is never a column the user asked for.
 */
export function dataColumns(
  rows: Array<Record<string, unknown>>,
  declared?: unknown,
  sample = 20,
): string[] {
  if (Array.isArray(declared) && declared.length && declared.every((c) => typeof c === "string")) {
    return declared as string[];
  }
  const seen = new Set<string>();
  for (const row of rows.slice(0, sample)) {
    for (const key of Object.keys(row)) {
      if (key !== "attributes") seen.add(key);
    }
  }
  return Array.from(seen);
}

/** Read `column` off a row, following dotted paths (`Account.Name`) for SOQL joins. */
export function readDataCell(row: Record<string, unknown>, column: string): unknown {
  if (Object.prototype.hasOwnProperty.call(row, column)) return row[column];
  if (!column.includes(".")) return undefined;
  let current: unknown = row;
  for (const part of column.split(".")) {
    if (!current || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Longest text a single cell may contribute. A nested object (or a Long Text Area
 * field) stringifies to arbitrarily many characters, and one such value lands in a
 * table row, a CSV field, AND — since the exporter renders through here — a line of
 * the exported .docx. The card truncated it visually with CSS; the exported file had
 * no such mercy.
 */
export const CELL_CHAR_CAP = 500;

/** One cell as display text — a related record shows its Name, not its JSON. */
export function displayCell(value: unknown): string {
  if (value == null) return "";
  let text: string;
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    text = typeof obj.Name === "string" ? obj.Name : JSON.stringify(value);
  } else {
    text = String(value);
  }
  if (typeof text !== "string") return "";
  // The ellipsis is part of the contract: a silently-cut value reads as the whole
  // value, which is the one thing a truncation must never look like.
  return text.length > CELL_CHAR_CAP ? `${text.slice(0, CELL_CHAR_CAP)}…` : text;
}
